import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Map } from 'react-map-gl';
import { useMapContext } from '../../contexts/MapContext';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import "mapbox-gl/dist/mapbox-gl.css";

import { 
    handleDrawCreate, 
    handleDrawMouseDown, 
    clearAllPolygons,
    handleMouseDown,
    handleMouseUp,
    handleMapClickWithBrush
 } from './MapDisplayHelpers';
import {useLIDContext} from "../../contexts/LIDContext";


const MapDisplay = ({ children }) => {
    const {
        mapRef, 
        handleMapClick,
        editMode,
        mouseDown, 
        setMouseDown,
        brushMode, 
        eraserMode,
        handMode,
        polygonMode,
        selectedStormOption,
        userData,
        devPlanID,
        containPlan,
        geoJSONData,
        clearPolygonsTrigger,
        outFileExists,
        planIsRunning,
    } = useMapContext();
    const {
        setBrushedSubcatchments,
        setBrushedFeaturesData,
    } = useLIDContext();

    const touchedSubcatchments = React.useRef(new Set()); // store subcatchment names
    const touchedProperties = React.useRef({});    // store entire subcatchment property objects
    const drawRef = useRef(null);
    const [drawActive, setDrawActive] = useState(false);
    const lastTouchedRef = useRef(null);

    useEffect(() => {
        if (!mapRef.current) return;
        
        const map = mapRef.current?.getMap?.();
        if (!map) return;

        if (polygonMode && !drawActive) {
            const draw = new MapboxDraw({
                displayControlsDefault: false,
                controls: {
                    polygon: false,
                    trash: false,
                    point: false,
                    line_string: false,
                    combine_features: false,
                    uncombine_features: false
                },
                defaultMode: 'draw_polygon',
                styles: [
                    {
                        "id": "gl-draw-line",
                        "type": "line",
                        "filter": ["all", ["==", "$type", "LineString"], ["!=", "mode", "static"]],
                        "layout": {
                            "line-cap": "round",
                            "line-join": "round"
                        },
                        "paint": {
                            "line-color": "#3b9ddd",
                            "line-dasharray": [0.2, 2],
                            "line-width": 2
                        }
                    },
                    {
                        "id": "gl-draw-polygon-fill",
                        "type": "fill",
                        "filter": ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
                        "paint": {
                            "fill-color": "#3b9ddd",
                            "fill-outline-color": "#3b9ddd",
                            "fill-opacity": 0.3
                        }
                    },
                    {
                        "id": "gl-draw-point",
                        "type": "circle",
                        "filter": ["all", ["==", "$type", "Point"], ["==", "meta", "vertex"]],
                        "paint": {
                            "circle-radius": 5,
                            "circle-color": "#fff",
                            "circle-stroke-color": "#3b9ddd",
                            "circle-stroke-width": 2
                        }
                    }
                ]
            });
            
            map.addControl(draw);
            drawRef.current = draw;
            setDrawActive(true); 

            const drawCreateHandler = (e) => handleDrawCreate({
                e, mapRef, drawRef, geoJSONData, setBrushedSubcatchments, setBrushedFeaturesData
            });
            const mouseDownHandler = (e) => handleDrawMouseDown({e, polygonMode, mapRef, drawRef});
            
            drawRef.current.handlers = {
                drawCreateHandler,
                mouseDownHandler
            };

            map.on('draw.create', drawCreateHandler);
            map.on('mousedown', mouseDownHandler);
            document.addEventListener('keydown', handleKeyDown);

            map.getCanvas().style.cursor = 'crosshair';
        } else if (!polygonMode && drawActive) {
            if (drawRef.current) {
                const map = mapRef.current?.getMap?.();
                
                if (map && drawRef.current.handlers) {
                    map.off('draw.create', drawRef.current.handlers.drawCreateHandler);
                    map.off('mousedown', drawRef.current.handlers.mouseDownHandler);
                }
                
                document.removeEventListener('keydown', handleKeyDown);
                
                map.removeControl(drawRef.current);
                drawRef.current = null;
                setDrawActive(false);
                map.getCanvas().style.cursor = '';
            }
        }

        return () => {
            if (map && drawRef.current) {
                if (drawRef.current.handlers) {
                    map.off('draw.create', drawRef.current.handlers.drawCreateHandler);
                    map.off('mousedown', drawRef.current.handlers.mouseDownHandler);
                }
                
                document.removeEventListener('keydown', handleKeyDown);
                
                try {
                    map.removeControl(drawRef.current);
                } catch (e) {
                    console.warn('Error removing draw control:', e);
                }
                
                drawRef.current = null;
                setDrawActive(false);
            }
        };
    }, [polygonMode, mapRef]);

    const handleKeyDown = (e) => {
        if (!polygonMode || !drawRef.current) return;
        if (e.key === 'Escape') {
            clearAllPolygons(drawRef);
        }
    };




    useEffect(() => {
        const floodingMapExist = outFileExists || planIsRunning; //userData && containPlan(selectedStormOption, userData.id, devPlanID);

        const cursor = document.createElement('div');
        cursor.style.position = 'absolute';
        cursor.style.width = '25px'; 
        cursor.style.height = '25px'; 
        cursor.style.border = '3px solid #1E90FF'; 
        cursor.style.borderRadius = '50%';
        cursor.style.pointerEvents = 'none';
        cursor.style.zIndex = '9999';

        if (editMode && !floodingMapExist) {
            // Only show brush cursor when in brush mode, not polygon mode
            if (brushMode || eraserMode) {

                document.body.appendChild(cursor);

                let animationFrameId = null;
                const moveCursor = (e) => {
                    if (animationFrameId) cancelAnimationFrame(animationFrameId);
                    animationFrameId = requestAnimationFrame(() => {
                        cursor.style.left = `${e.clientX - 12}px`;
                        cursor.style.top = `${e.clientY - 12}px`;
                    });

                };

                const brushHandler = (e) => {
                    if (!mouseDown || (!brushMode && !eraserMode)) return;
                    if (!mapRef.current) return;

                    const map = mapRef.current?.getMap?.();
                    if (!map) return;



                    const canvas = map.getCanvas();
                    const canvasRect = canvas.getBoundingClientRect();

                    const point = [
                        e.clientX - canvasRect.left,
                        e.clientY - canvasRect.top,
                    ];

                    // Define your desired radius in pixels
                    const radius = 15; // 15px radius

                    // Create a bounding box around the point
                    const bbox = [
                        [point[0] - radius, point[1] - radius], // [minX, minY]
                        [point[0] + radius, point[1] + radius]  // [maxX, maxY]
                    ];

                    const features = map.queryRenderedFeatures(bbox, {
                        layers: ['subcatchments'],
                    });

                    if (features.length > 0) {
                        // Only update if this is a new subcatchment
                        if (features[0].properties.NAME !== lastTouchedRef.current) {
                            lastTouchedRef.current = features[0].properties.NAME;

                            for (let i = 0; i < features.length; i++) {
                                const feature = features[i];
                                const name = feature.properties.NAME;

                                touchedSubcatchments.current.add(name);
                                touchedProperties.current[name] = feature.properties;

                                setBrushedSubcatchments((prev) => {
                                    const newSet = new Set(prev);
                                    eraserMode? newSet.delete(name) : newSet.add(name);
                                    return newSet;
                                })

                                setBrushedFeaturesData((prev) => {
                                    const newData = {...prev};
                                    eraserMode? delete newData[name] : newData[name] = feature.properties;
                                    return newData;
                                })


                            }
                            /*
                            setBrushedSubcatchments((prev) => {
                                const newSet = new Set(prev);
                                if (eraserMode) {
                                    // Remove items
                                    touchedSubcatchments.current.forEach(name => newSet.delete(name));
                                } else {
                                    // Add items
                                    touchedSubcatchments.current.forEach(name => newSet.add(name));
                                }
                                return newSet;
                            })

                            setBrushedFeaturesData((prev) => {
                                console.log('prev:', prev);
                                const newData = {...prev};
                                if (eraserMode) {
                                    // Remove properties
                                    Object.keys(touchedProperties.current).forEach(name => delete newData[name]);
                                } else {
                                    // Add properties
                                    Object.assign(newData, touchedProperties.current);
                                }
                                console.log('newData:', newData);
                                return newData;
                            });
                            // Reset the touched subcatchments and properties
                            touchedSubcatchments.current = new Set();
                            touchedProperties.current = {};

                             */
                        }

                    }
                };


                document.addEventListener('mousemove', moveCursor);
                document.addEventListener('mousemove', brushHandler);


                return () => {
                    document.removeEventListener('mousemove', moveCursor);
                    document.removeEventListener('mousemove', brushHandler);

                    cursor.remove();
                    // document.body.style.cursor = 'default';
                };

            } else {
                cursor.remove();
            }
        } else {
            cursor.remove();
        }
    }, [editMode, mouseDown, brushMode, eraserMode, selectedStormOption, userData, devPlanID, containPlan]);

    // This useEffect is to reset the cursor to default when editMode is false.
    useEffect(() => {
        const map = mapRef.current?.getMap?.();
        if (!map) return;

        if (!editMode) {
            map.getCanvas().style.cursor = '';
        }
    }, [editMode, mapRef]);

    useEffect(() => {
        if (clearPolygonsTrigger > 0 && drawRef.current) {
            clearAllPolygons(drawRef);  
        }
    }, [clearPolygonsTrigger]);

    const handleMapLoad = useCallback((event) => {
        const map = event.target; // The map instance is available as event.target
        const imageUrl = '/lines.png';

        map.loadImage(imageUrl, (error, image) => {
            if (error) {
                console.error('Error loading image:', error);
                return;
            }
            if (!map.hasImage('brushPattern')) {
                map.addImage('brushPattern', image);
            }
        });
    }, []);



    /*
    // Rain animation -- turn back on once planIsRunning is airtight.
    useEffect(() => {
        const map = mapRef.current?.getMap?.();
        if (!map) return;
        if (planIsRunning) {
            map.setRain({
                "density": 0.5,
                "intensity": 1.0,
                "color": "#d0d9f7",
                "opacity": 0.5,
                "vignette": 1.0,
                "vignette-color": "#afd2cf",
                "direction": [10, 90],
                "droplet-size": [1.6, 13.2],
                "distortion-strength": 0.7,
                "center-thinning": 0
            });
        } else {
            map.setRain(null);
        }


    }, [mapRef, planIsRunning]);
     */


    return (
        <Map
            ref={mapRef}
            mapboxAccessToken="pk.eyJ1IjoibGVicmFuay11bSIsImEiOiJjbG82Ymxxd2wwaW16Mm1sbW1zZDNibm9mIn0.ioLjzOX9ELIsHmfp1JW42w"
            initialViewState={{
                longitude: -83.1,
                latitude: 42.35,
                zoom: 10.8,
                pitch: 45,
            }}
            style={{ width: '100%', height: '100%' }}
            mapStyle="mapbox://styles/mapbox/dark-v11"

            dragPan={handMode}

            onMouseDown={() => handleMouseDown(setMouseDown)}
            onMouseUp={() => handleMouseUp(setMouseDown)}
            onMouseLeave={() => handleMouseUp(setMouseDown)}
            onLoad={handleMapLoad}
            onClick={(event) => {
                if (editMode && !polygonMode) {
                    handleMapClickWithBrush({
                        e: event,
                        editMode,
                        brushMode,
                        eraserMode,
                        mapRef,
                        setBrushedSubcatchments,
                        setBrushedFeaturesData
                    });
                } else if (!polygonMode) {
                    console.log('Normal click');
                    handleMapClick(event); // Only handle single-click selection in normal mode
                }
            }}
        >
            {children}
        </Map>
    );
};

export default MapDisplay;


